// Riordina's drag: a handle in place of the switch. A mouse drags the row (HTML5 drag events); a finger drags the
// handle (pointer events, touch-action none), so a scroll never turns into a drag outside the mode. The arrow keys move
// a focused handle. Every drop saves at once (EDT-02). useReorder holds it, so a category's items, its sub-categories
// and the tab's category sections all move the same way.
import { useEffect, useRef, useState, type DragEvent, type KeyboardEvent, type PointerEvent } from "react";
import { MenuIcon } from "@/components/icons";
import { cn } from "@/utils/cn";

/** What a reorderable row shows: an item or a category. */
export interface ReorderRow {
  id: string;
  name: string;
  hidden: boolean;
}

const moved = (ids: string[], id: string, to: number): string[] => {
  const rest = ids.filter((x) => x !== id);
  rest.splice(Math.max(0, Math.min(to, rest.length)), 0, id);
  return rest;
};

/** The order shown while dragging, the row's drag props and the handle's, over the ids in their saved order. */
export function useReorder(ids: string[], onReorder: (ids: string[]) => void) {
  const incomingKey = ids.join(",");
  const [order, setOrder] = useState<string[]>(ids);
  const [dragging, setDragging] = useState<string | null>(null);
  const start = useRef<string>(incomingKey);

  const draggingRef = useRef<string | null>(null);
  draggingRef.current = dragging;

  // A new server order wins unless a drag is under way. Keyed on the order only, so ending a drag does not flash the
  // old order back before the saved one arrives.
  useEffect(() => {
    if (!draggingRef.current) setOrder(incomingKey ? incomingKey.split(",") : []);
  }, [incomingKey]);

  const begin = (id: string) => {
    start.current = order.join(",");
    setDragging(id);
  };
  const finish = (final: string[]) => {
    setDragging(null);
    if (final.join(",") !== start.current) onReorder(final);
  };
  const hover = (overId: string) => {
    if (!dragging || overId === dragging) return;
    setOrder((current) => moved(current, dragging, current.indexOf(overId)));
  };

  // Mouse: HTML5 drag and drop on the whole row.
  const rowProps = (id: string) => ({
    "data-reorder-id": id,
    draggable: true,
    onDragStart: (e: DragEvent) => {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", id);
      begin(id);
    },
    onDragOver: (e: DragEvent) => {
      e.preventDefault();
      hover(id);
    },
    onDrop: (e: DragEvent) => e.preventDefault(),
    onDragEnd: () => finish(order),
  });

  // Touch and pen: the handle captures the pointer and the row under the finger takes the dragged row's place.
  const onPointerUp = (e: PointerEvent) => {
    if (!dragging || e.pointerType === "mouse") return;
    finish(order);
  };
  const handleProps = (id: string) => ({
    onPointerDown: (e: PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType === "mouse") return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      begin(id);
    },
    onPointerMove: (e: PointerEvent) => {
      if (!dragging || e.pointerType === "mouse") return;
      const row = document.elementFromPoint(e.clientX, e.clientY)?.closest<HTMLElement>("[data-reorder-id]");
      const overId = row?.dataset.reorderId;
      if (overId) hover(overId);
    },
    onPointerUp,
    onPointerCancel: onPointerUp,
    onKeyDown: (e: KeyboardEvent) => {
      const step = e.key === "ArrowUp" ? -1 : e.key === "ArrowDown" ? 1 : 0;
      if (!step) return;
      e.preventDefault();
      const next = moved(order, id, order.indexOf(id) + step);
      start.current = order.join(",");
      setOrder(next);
      if (next.join(",") !== start.current) onReorder(next);
    },
  });

  return { order, dragging, rowProps, handleProps };
}

export type ReorderHandleProps = ReturnType<ReturnType<typeof useReorder>["handleProps"]>;

/** The grip a finger drags and the arrow keys move, named for what it moves. */
export function ReorderHandle({ label, handle }: { label: string; handle: ReorderHandleProps }) {
  return (
    <button
      type="button"
      aria-label={label}
      {...handle}
      className="grid h-[40px] w-[44px] shrink-0 place-items-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 [touch-action:none]"
    >
      <MenuIcon className="w-5 h-5" />
    </button>
  );
}

interface Props {
  items: ReorderRow[];
  onReorder: (ids: string[]) => void;
  /** The handle's name: "Sposta <item>" unless given. */
  moveLabel?: (name: string) => string;
  label?: string;
}

export function ReorderList({ items, onReorder, moveLabel = (name) => `Sposta ${name}`, label = "Trascina per riordinare" }: Props) {
  const { order, dragging, rowProps, handleProps } = useReorder(items.map((i) => i.id), onReorder);
  const byId = new Map(items.map((i) => [i.id, i]));

  return (
    <ul className="divide-y divide-gray-100" aria-label={label}>
      {order.map((id) => {
        const item = byId.get(id);
        if (!item) return null;
        return (
          <li
            key={id}
            {...rowProps(id)}
            className={cn(
              "flex items-center gap-2 py-2 select-none cursor-grab active:cursor-grabbing transition-colors",
              dragging === id && "bg-indigo-50/70 rounded-lg",
            )}
          >
            <span className={cn("min-w-0 flex-1 pl-1 text-[13px] font-medium leading-[18px] text-gray-900 line-clamp-2 [overflow-wrap:anywhere]", item.hidden && "opacity-60")}>
              {item.name}
            </span>
            <ReorderHandle label={moveLabel(item.name)} handle={handleProps(id)} />
          </li>
        );
      })}
    </ul>
  );
}
